/**
 * The order pages of a store a shopper can no longer shop in.
 *
 * Jake, OD-20261016-3 follow-up (2026-10-16): for a CLOSED or ARCHIVED store,
 *   (C) a SIGNED-IN shopper still reads their own orders, and
 *   (B) anyone NOT signed in gets the "email me my order links" form --
 *       and nothing about the store.
 *
 * The session gate lets the two order pages (`ORDER_PAGE_ROUTE_NAMES`) render
 * WITHOUT a store session when the store does not resolve, is switched off,
 * archived or closed (router/ensureStorefrontSession.js), exactly as it
 * already did for the emailed order link. It says so by providing
 * `STOREFRONT_NO_STORE_SESSION` (true), and each page asks
 * `ownOrdersPageMode` what to render:
 *
 *   "store"      -- the gate entered the store: the page as it always was.
 *   "own-orders" -- no store session, but a PERSONAL shopper session: read the
 *                   shopper's own orders. The API admits exactly those reads
 *                   for a shopper of an inactive store and scopes them to the
 *                   caller (api/src/models/auth_decorators.py,
 *                   `_OWN_ORDER_READ_ENDPOINTS`); this is presentation only.
 *   "lookup"     -- no store session and no personal session: the order-link form
 *                   only, and no request is made.
 */

import { isAnonymousStorefrontSession } from "./anonymousStorefrontSession";
import { isPrivilegedSession, ROLE_NAME_KEY, SHOPPER_ROLE_NAME } from "./privilegedSession";

/**
 * Provided by StorefrontSessionGate: true when it rendered the page with NO
 * store session (the store is closed, switched off, archived or unresolvable).
 * Injected with a `false` default, so a page mounted outside the gate behaves
 * as it always did.
 */
export const STOREFRONT_NO_STORE_SESSION = Symbol("storefrontNoStoreSession");

/** The pages the gate renders without a store session (with `isEmailedOrderLink`). */
export const ORDER_PAGE_ROUTE_NAMES = Object.freeze([
  "storefront_orders_list",
  "storefront_order_detail",
]);

export function isOrderPage(route) {
  return ORDER_PAGE_ROUTE_NAMES.includes(route?.name);
}

function readLocal(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * The account id of a SIGNED-IN shopper session, or null.
 *
 * Null for the store's shared anonymous cart session, any back-office or
 * impersonating session, no session, and unreadable storage (where the
 * anonymous marker cannot be read, `isAnonymousStorefrontSession` already
 * answers "anonymous"): under doubt the page shows the lookup form.
 */
export function personalShopperAccountId() {
  if (!readLocal("token")) return null;
  if (readLocal(ROLE_NAME_KEY) !== SHOPPER_ROLE_NAME) return null;
  if (isPrivilegedSession() || isAnonymousStorefrontSession()) return null;
  const id = Number(readLocal("userId"));
  return Number.isInteger(id) && id > 0 ? id : null;
}

export function ownOrdersPageMode({ noStoreSession, personalAccountId }) {
  if (!noStoreSession) return "store";
  return personalAccountId != null ? "own-orders" : "lookup";
}
